// Central persona & scenario catalog — Akamai × Jio Financial Services simulations.
// Mirrors backend scenario packages in /backend/scenarios/.

export const PERSONA_META = {
  // --- SDR Edition (akamai_jfs_sdr) ---
  rohit_malhotra: {
    name: "Rohit Malhotra", short: "RM", scenario: "akamai_jfs_sdr",
    role: "Head of Application Security, Jio Financial Services",
    archetype: "The Overloaded Owner / Economic Buyer", difficulty: "Medium",
    focus_areas: ["Identify Pain (I)", "Metrics (M)"], focus: ["I", "M"],
    background: "Fourteen years in application security across two Indian private banks and a payments unicorn. Carries the CISO's verified-API-inventory mandate with 9 engineers and no new tooling budget approved yet.",
    approach: "Busy, direct, mildly weary — but fair. Gives sellers about 90 seconds to prove they did their homework. Responds to specific, relevant questions; shuts down on generic pitches.",
    positive_triggers: ["Evident pre-call research", "Sharp discovery questions", "Small concrete first step"],
    negative_triggers: ["Scripted cold pitch", "Feature dumping", "Premature hard close"],
  },
  priya_deshpande: {
    name: "Priya Deshpande", short: "PD", scenario: "akamai_jfs_sdr",
    role: "DevSecOps Lead, JioFinance Digital Platform",
    archetype: "The Hands-On Ally / Champion Candidate", difficulty: "Easy",
    focus_areas: ["Decision Criteria (D1)", "Champion (C1)"], focus: ["D1", "C1"],
    background: "Owns security tooling in the JioFinance CI/CD pipelines. Personally maintains the API inventory spreadsheet and knows exactly how wrong it is — she wants this problem solved more than anyone.",
    approach: "Warm, talkative, technically fluent. Asks practical questions — integrations, false-positive rates, how discovery actually works. Goes cold if she senses she's being used to reach her boss.",
    positive_triggers: ["Treating her as expert", "Practical integration answers", "Genuine curiosity"],
    negative_triggers: ["Going over her head", "Condescension", "Dodging technical questions"],
  },
  vikram_bose: {
    name: "Vikram Bose", short: "VB", scenario: "akamai_jfs_sdr",
    role: "Senior Sourcing Analyst, Reliance Group Procurement",
    archetype: "The Process Guardian / Blocker", difficulty: "Hard",
    focus_areas: ["Paper Process (P)", "Competition (C2)"], focus: ["P", "C2"],
    background: "Nine years in Reliance group procurement; runs first-line commercial screening. Trained to challenge every vendor claim and benchmark every price. Not hostile, just professionally unimpressed.",
    approach: "Polite, procedural, immovable on process. Quotes the public US$150K entry price. Softens for capped, well-scoped assessments with clear deliverables and no auto-renewal.",
    positive_triggers: ["Capped scoped pricing", "Clear deliverables list", "Data-residency answers"],
    negative_triggers: ["Urgency pressure", "Vague scope", "Auto-renewal fine print"],
  },

  // --- Enterprise Sales Edition (akamai_jfs_sales) ---
  devika_menon: {
    name: "Devika Menon", short: "DM", scenario: "akamai_jfs_sales",
    role: "Group CISO, Jio Financial Services",
    archetype: "The Accountable Executive / Economic Buyer", difficulty: "Hard",
    focus_areas: ["Identify Pain (I)", "Metrics (M)"], focus: ["I", "M"],
    background: "Twenty-two years in Indian banking security; former CISO of a top-three private bank. Carries the board's 90-day API remediation mandate — her regulatory standing is directly on the line.",
    approach: "Composed, senior, time-poor. Speaks in regulatory and risk language. Opens hard, softens only for sellers who quantify. Allergic to fear-mongering — expects a point of view on HER 90-day problem.",
    positive_triggers: ["Quantified risk reduction", "Regulatory fluency", "Phased 90-day plan"],
    negative_triggers: ["Generic security pitch", "Fear-mongering without numbers", "Overpromising"],
  },
  arjun_nair: {
    name: "Arjun Nair", short: "AN", scenario: "akamai_jfs_sales",
    role: "Head of Platform Engineering, JioFinance Digital Platform",
    archetype: "The Builder / Champion Candidate", difficulty: "Medium",
    focus_areas: ["Decision Criteria (D1)", "Champion (C1)"], focus: ["D1", "C1"],
    background: "Twelve years inside the Jio ecosystem; built the JioFinance super-app's shared API layer. Privately knows the gateway estate is messier than anyone upstairs admits.",
    approach: "Fast, technical, informal. Tests sellers with architecture specifics — gateways, latency, agents, data paths. Respects engineers, detests slideware. If won over, becomes the loudest internal advocate.",
    positive_triggers: ["Technical depth", "Agentless zero-latency respect", "Respecting in-house work"],
    negative_triggers: ["Rip-and-replace talk", "Rubbishing his stack", "Marketing slideware"],
  },
  sameer_kulkarni: {
    name: "Sameer Kulkarni", short: "SK", scenario: "akamai_jfs_sales",
    role: "VP Strategic Sourcing & Third-Party Risk, Reliance Group Procurement",
    archetype: "The Gatekeeper / Blocker", difficulty: "Very Hard",
    focus_areas: ["Paper Process (P)", "Competition (C2)"], focus: ["P", "C2"],
    background: "Eighteen years negotiating telecom, cloud, and security contracts worth thousands of crores. Believes every vendor's first quote is theatre. Personally accountable for auditable vendor-risk sign-off.",
    approach: "Blunt, transactional, enjoys silence as a weapon. Opens with price before value; anchors on the AWS Marketplace listing. Warms — slightly — to phased commitments that cap his downside.",
    positive_triggers: ["Transparent pricing logic", "Data-residency clarity", "Phased commercial structure"],
    negative_triggers: ["Evasive on price", "Artificial urgency", "Bypassing procurement"],
  },
  farah_sheikh: {
    name: "Farah Sheikh", short: "FS", scenario: "akamai_jfs_sales",
    role: "Group Head – Technology & Operational Risk, JFS",
    archetype: "The Regulator's Voice / Influencer", difficulty: "Hard",
    focus_areas: ["Metrics (M)", "Decision Process (D2)"], focus: ["M", "D2"],
    background: "Former RBI supervisory officer. Drafts the technology sections of the board risk register and faces the Q4 SEBI CSCRF audit. Unmoved by product enthusiasm; moved by auditability.",
    approach: "Precise, formal, quietly probing. Asks compliance-mapping questions: which control maps to which CSCRF clause, what evidence a regulator would see, how fast incident reports could be produced.",
    positive_triggers: ["Compliance mapping specifics", "Audit-ready evidence", "Risk quantified in rupees"],
    negative_triggers: ["Compliance name-dropping", "Vague framework claims", "Minimizing regulatory risk"],
  },
};

export const SCENARIO_BRIEFS = {
  sdr: {
    displayName: "Akamai × Jio Financial Services — SDR Edition",
    shortName: "SDR Edition",
    difficulty: "Foundational",
    client: "Jio Financial Services (JFS)",
    clientDesc: "₹1.6 lakh crore Indian digital-finance group · 25M+ users · 4 regulated entities",
    problem: "JFS's AppSec team must produce a verified inventory of every API within one quarter — and its own spreadsheet is off by more than half: 4,700+ live endpoints vs 1,900 documented, with a deprecated KYC endpoint found live 14 months after retirement.",
    yourRole: "Sales Development Representative, Enterprise Security at Akamai. Earn the next conversation: research-led relevance, sharp discovery questions, and a credible small first step — a 6-week API Discovery Assessment (~₹1.3 cr, capped).",
    keyPoints: [
      "Your WAF protects the doors you told it about — the risk is the doors nobody registered",
      "Six weeks of automated discovery beats eleven months of manual reconciliation",
      "This isn't a platform decision — it's a capped six-week assessment",
    ],
    personas: ["rohit_malhotra", "priya_deshpande", "vikram_bose"],
    startingPersona: "rohit_malhotra",
    phases: {
      P1: "Research & Rapport", P2: "Qualification & Pain Discovery",
      P3: "Value & Champion Building", P4: "Commitment & Next Steps",
    },
  },
  sales: {
    displayName: "Akamai × Jio Financial Services — Enterprise Sales Edition",
    shortName: "Enterprise Sales Edition",
    difficulty: "Advanced",
    client: "Jio Financial Services (JFS)",
    clientDesc: "₹1.6 lakh crore Indian digital-finance group · 25M+ users · 4 regulated entities",
    problem: "The board has mandated a 90-day API security remediation after a BOLA flaw exposed ~48,000 customers' portfolio records for 19 days. 60% of the API estate is undocumented across 11 gateways and 4 entities — and SEBI's CSCRF audit lands in Q4.",
    yourRole: "Major Account Executive, Enterprise Security at Akamai. Sell a governance outcome to a board deadline — a multi-entity platform subscription (₹8–15 cr/yr) — consultative, quantified, regulator-fluent.",
    keyPoints: [
      "You cannot secure — or certify to RBI and SEBI — APIs you don't know exist",
      "The BOLA near-miss was a valid authenticated call; no WAF signature would stop it",
      "Verified inventory in weeks, inside the 90 days — agentless, zero latency",
    ],
    personas: ["devika_menon", "arjun_nair", "sameer_kulkarni", "farah_sheikh"],
    startingPersona: "devika_menon",
    phases: {
      P1: "Discovery & Risk Diagnosis", P2: "Stakeholder Qualification & Alignment",
      P3: "Technical Validation & Proposal", P4: "Commercial Commitment & Closure",
    },
  },
};

export const DISCLAIMER_TEXT =
  "This is a fictional training scenario. Persona names are invented; company facts are used for training realism only and include fictional elements. Nothing here describes real events at the organizations named.";

export const getPersonaMeta = (pid) =>
  PERSONA_META[pid] || { name: pid || "Stakeholder", short: "?", role: "", archetype: "", focus_areas: [], focus: [], positive_triggers: [], negative_triggers: [], background: "", approach: "", difficulty: "" };

// Briefs are keyed by role_id (CR-001: one scenario 'akamai_jfs', multiple role profiles)
export const getScenarioBrief = (scenarioId, roleId) => SCENARIO_BRIEFS[roleId] || SCENARIO_BRIEFS.sdr;

export const getPhaseName = (phaseId, scenarioId, roleId) =>
  getScenarioBrief(scenarioId, roleId).phases[phaseId] || phaseId;

export const getScenarioPersonas = (scenarioId, roleId) => getScenarioBrief(scenarioId, roleId).personas;
